import type React from 'react'
import { getTableList, ReportOption } from '@/analytics/reportRegistry'
import PlusIcon from '@mui/icons-material/Add'
import AccountIcon from '@mui/icons-material/AccountCircle'
import AdminIcon from '@mui/icons-material/AdminPanelSettings'
import BuildingIcon from '@mui/icons-material/Apartment'
import ClipboardListIcon from '@mui/icons-material/Assignment'
import SparklesIcon from '@mui/icons-material/AutoAwesome'
import WhiteLabelIcon from '@mui/icons-material/BrandingWatermark'
import WrenchIcon from '@mui/icons-material/Build'
import CalendarIcon from '@mui/icons-material/CalendarMonth'
import TicketIcon from '@mui/icons-material/ConfirmationNumberOutlined'
import FileTextIcon from '@mui/icons-material/Description'
import NetworkIcon from '@mui/icons-material/DeviceHub'
import FeedbackIcon from '@mui/icons-material/Feedback'
import FlagIcon from '@mui/icons-material/Flag'
import ScaleIcon from '@mui/icons-material/Gavel'
import UserCheckIcon from '@mui/icons-material/HowToReg'
import PackageIcon from '@mui/icons-material/Inventory2'
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks'
import TagIcon from '@mui/icons-material/LocalOffer'
import MapIcon from '@mui/icons-material/Map'
import BellIcon from '@mui/icons-material/Notifications'
import PaletteIcon from '@mui/icons-material/Palette'
import UsersIcon from '@mui/icons-material/People'
import GlobeIcon from '@mui/icons-material/Public'
import QrCode2Icon from '@mui/icons-material/QrCode2'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import RateReviewIcon from '@mui/icons-material/RateReview'
import ShieldIcon from '@mui/icons-material/Security'
import SettingsIcon from '@mui/icons-material/Settings'
import StickyNote2Icon from '@mui/icons-material/StickyNote2'
import DatabaseIcon from '@mui/icons-material/Storage'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import ShieldCheckIcon from '@mui/icons-material/VerifiedUser'

export type IconComponent = React.ElementType<{
  className?: string
  fontSize?: 'inherit' | 'small' | 'medium' | 'large'
}>

export type ViewMode = 'building' | 'portfolio' | 'activate'

export interface NavItem {
  name: string
  icon: IconComponent
  path?: string
  permissionKey?: string
  requiresProperty?: boolean
  isAction?: boolean
  excludeFromOffice?: boolean
  excludeFromIndustrial?: boolean
}

export interface AddButton {
  label: string
  path: string
  permissionKey?: string
  requiresProperty?: boolean
}

export interface Category {
  title: string
  description: string
  icon: IconComponent
  addButtonLabel?: string
  addPath?: string
  addPermissionKey?: string
  addRequiresProperty?: boolean
  addButtons?: AddButton[]
  items: NavItem[]
}

/**
 * Analytics tiles name a report by its registry `value` rather than repeating a
 * literal path, so the launcher cannot drift from the sidebar and the category
 * tabs — label, path, and permission all resolve from reportRegistry. Tiles that
 * hard-coded a path are how the "PM Dashboard" tile went on opening the
 * superseded Preventative Maintenance page for a month after it was replaced.
 */
const REPORTS = new Map((getTableList() as ReportOption[]).map((option) => [option.value, option] as const))
const ACTIVATE_PERMISSION = 'ActivateCustomerFeedback'
/**
 * Reports with no permission key of their own still sit behind a section guard:
 * every /analytics/dashboard/* route inherits AnalyticsDashboards, and every
 * portfolio dashboard is verified against ViewAnalytics (useVerifyDashboard).
 */
const sectionPermission = (path?: string) => {
  if (path?.startsWith('/portfolioAnalytics/globalDashboard')) return 'ViewAnalytics'
  if (path?.startsWith('/analytics/')) return 'AnalyticsDashboards'
  return undefined
}
const report = (value: string, item: Omit<NavItem, 'path' | 'name'> & { name?: string }): NavItem => {
  const option = REPORTS.get(value)
  // A renamed or removed registry value would otherwise leave a pathless tile
  // that quietly reads "Coming soon" — surface it while developing instead.
  if (!option && import.meta.env.DEV) {
    throw new Error(`dashboardConfig: no report registered for "${value}"`)
  }
  return {
    ...item,
    name: item.name ?? option?.label ?? value,
    path: option?.path,
    permissionKey: item.permissionKey ?? option?.permissionKey ?? sectionPermission(option?.path),
  }
}

export const CATEGORY_CONFIG: Category[] = [
  {
    title: 'BUILDING INFO',
    description: 'Building, people, vendor, and compliance control plane',
    icon: BuildingIcon,
    addButtonLabel: 'Announcement',
    addPath: '/buildingInfo/branding/announcements',
    addRequiresProperty: true,
    items: [
      {
        name: 'Announcements',
        icon: BellIcon,
        path: '/buildingInfo/branding/announcements',
        permissionKey: 'ManagePropertyInfo',
        requiresProperty: true,
      },
      {
        name: 'Branding',
        icon: PaletteIcon,
        path: '/buildingInfo/branding/branding',
        permissionKey: 'Branding',
        requiresProperty: true,
      },
      {
        name: 'Building Info',
        icon: BuildingIcon,
        path: '/buildingInfo/general/basicInfo',
        permissionKey: 'ManagePropertyInfo',
        requiresProperty: true,
      },
      {
        // The Preferences section: country and timezone defaults for the building.
        name: 'Countries and Timezones',
        icon: GlobeIcon,
        path: '/buildingInfo/general/preferences',
        permissionKey: 'BuildingPIN',
        requiresProperty: true,
      },
      {
        name: 'Document Library',
        icon: FileTextIcon,
        path: '/buildingInfo/branding/documentLibrary',
        permissionKey: 'ManagePropertyInfo',
        requiresProperty: true,
      },
      // Marketing tile suppressed (2026-08-22 nav reorg) — route stays live.
      {
        name: 'Property Team',
        icon: UsersIcon,
        path: '/contacts/contactList',
        permissionKey: 'ManageContacts',
        requiresProperty: true,
      },
      {
        name: 'Property Characteristics',
        icon: BuildingIcon,
        path: '/buildingInfo/operations/propertyCharacteristics',
        permissionKey: 'ManagePropertyInfo',
        requiresProperty: true,
        excludeFromOffice: true,
      },
      {
        name: 'Supplier Contacts',
        icon: PackageIcon,
        path: '/contacts/vendorList',
        permissionKey: 'ManageContacts',
        requiresProperty: true,
      },
      {
        name: 'Tags',
        icon: TagIcon,
        path: '/buildingInfo/general/tags',
        permissionKey: 'BuildingPIN',
        requiresProperty: true,
      },
      {
        name: 'Tenant Company',
        icon: BuildingIcon,
        path: '/contacts/tenantLibrary',
        permissionKey: 'ManageContacts',
        requiresProperty: true,
      },
      {
        name: 'Tenant Contacts',
        icon: UsersIcon,
        path: '/contacts/tenantContacts',
        permissionKey: 'ManageContacts',
        requiresProperty: true,
      },
      {
        name: 'White Label',
        icon: WhiteLabelIcon,
        path: '/buildingInfo/branding/whiteLabel',
        permissionKey: 'WhiteLabel',
        requiresProperty: true,
      },
    ],
  },
  {
    title: 'ANALYTICS',
    description: 'Intelligence, reporting, subscriptions, and historical record',
    icon: TrendingUpIcon,
    addButtonLabel: 'Subscriber',
    addPath: '/notifications/subscriptions',
    items: [
      { name: 'Alerts', icon: BellIcon, path: '/notifications/alerts', permissionKey: 'AnalyticsAlerts' },
      {
        // Renders through the Building Info shell; the page is the Data Archive
        // tab strip's Building Terms & Conditions.
        name: 'Building Terms & Conditions',
        icon: FileTextIcon,
        path: '/buildingInfo/dataArchive/terms',
        permissionKey: 'BuildingTermsAndConditions',
        requiresProperty: true,
      },
      report('CapitalPlanning', { icon: TrendingUpIcon, isAction: true }),
      { name: 'Data Archive', icon: DatabaseIcon, path: '/dataArchive', permissionKey: 'ManageDateArchive' },
      report('LeaseAbstraction', { icon: TrendingUpIcon, isAction: true }),
      {
        name: 'Notifications & Alerts',
        icon: BellIcon,
        path: '/notifications/engine',
        requiresProperty: true,
      },
      {
        name: 'Reports',
        icon: FileTextIcon,
        path: '/dataArchive/reports',
        permissionKey: 'AnalyticsReports',
      },
      report('RoofAbstraction', { icon: TrendingUpIcon, isAction: true }),
      report('ComplianceRecords', { icon: ShieldCheckIcon, isAction: true }),
      {
        name: 'Subscriptions',
        icon: TrendingUpIcon,
        path: '/notifications/subscriptions',
        permissionKey: 'AnalyticsSubscriptions',
        isAction: true,
      },
    ],
  },
  {
    title: 'EQUIPMENT',
    description: 'System of record for all physical assets',
    icon: NetworkIcon,
    addButtonLabel: 'New',
    addPath: '/library/equipmentLibrary',
    addRequiresProperty: true,
    items: [
      // Registry label is 'Equipment Data Integrity Dashboard'; the card already
      // says EQUIPMENT, so the tile keeps the short form.
      report('EquipmentDataIntegrity', { name: 'Data Integrity', icon: TrendingUpIcon, isAction: true }),
      report('EquipmentDashboard', { icon: TrendingUpIcon, isAction: true, requiresProperty: true }),
      report('EquipmentLookup', { icon: TrendingUpIcon, isAction: true }),
      report('EquipmentTrend', { icon: TrendingUpIcon, isAction: true }),
      report('BathroomService', { icon: TrendingUpIcon, isAction: true }),
      report('PMCompliance', { icon: TrendingUpIcon, isAction: true }),
    ],
  },
  {
    title: 'PERFORMANCE',
    description: 'Operational execution layers',
    icon: ShieldCheckIcon,
    items: [
      report('Cleaning', { icon: TrendingUpIcon, excludeFromIndustrial: true, isAction: true }),
      report('CleaningIntelligence', { icon: TrendingUpIcon, excludeFromIndustrial: true, isAction: true }),
      report('Engineering', { icon: TrendingUpIcon, isAction: true }),
      report('EngineeringIntelligence', { icon: TrendingUpIcon, isAction: true }),
      report('InspectionRound', { icon: TrendingUpIcon, isAction: true }),
      report('SecurityPatrol', { icon: TrendingUpIcon, isAction: true }),
      report('SecurityIntelligence', { icon: TrendingUpIcon, isAction: true }),
      // Collection Dashboard tile suppressed (2026-08-22 nav reorg).
      report('DeficiencyTracker', { icon: TrendingUpIcon, excludeFromOffice: true, isAction: true }),
      report('TeamPerformance', { icon: TrendingUpIcon, isAction: true }),
    ],
  },
  {
    title: 'WORK ORDERS',
    description: 'Work creation, costing, and billing',
    icon: ClipboardListIcon,
    addButtonLabel: 'New',
    addPath: '/workOrderDashboard',
    items: [
      // One tile per Accounting tab (the page splits into General Info / GL &
      // Charge Codes / Materials & Labor). Gated on MaterialAndCost, the key
      // the sections themselves render behind — ManagePropertyInfo alone left
      // the tiles enabled and landed on a page with no section on it.
      {
        name: 'Billing Rates and Materials',
        icon: TagIcon,
        path: '/buildingInfo/accounting/materialsLabor',
        permissionKey: 'MaterialAndCost',
        requiresProperty: true,
      },
      {
        name: 'Charge Codes',
        icon: TagIcon,
        path: '/buildingInfo/accounting/glChargeCodes',
        permissionKey: 'MaterialAndCost',
        requiresProperty: true,
      },
      {
        // Building-level work order configuration (Building Info › Operations).
        // Sits here with the other two building-config tiles rather than on the
        // Building Info card, so the work order settings live beside the work
        // order dashboards they govern.
        name: 'Work Order Settings',
        icon: ClipboardListIcon,
        path: '/buildingInfo/operations/workOrders',
        permissionKey: 'WorkOrderSettings',
        requiresProperty: true,
      },
      report('AccountingDashboard', { icon: TrendingUpIcon, isAction: true }),
      report('FeedbackDashboard', { icon: TrendingUpIcon, isAction: true }),
      report('TenantHealthDashboard', { icon: TrendingUpIcon, isAction: true }),
      report('TenantPerformanceDashboard', { icon: TrendingUpIcon, isAction: true }),
      report('WorkOrderDashboard', { icon: TrendingUpIcon, isAction: true }),
      report('WorkOrderIntelligence', { icon: TrendingUpIcon, isAction: true }),
    ],
  },
  {
    title: 'BUILDING ACCESS',
    description: 'Audit trail of who entered the building and why',
    icon: UserCheckIcon,
    addButtonLabel: 'Visitor',
    addPath: '/visitorLog',
    items: [
      { name: 'Calendar', icon: CalendarIcon, path: '/calendar', permissionKey: 'Calendar' },
      {
        name: 'Certificate of Insurance',
        icon: ShieldIcon,
        path: '/buildingInfo/coi/coi',
        permissionKey: 'ManagePropertyInfo',
        requiresProperty: true,
      },
      report('InsuranceDashboard', { icon: TrendingUpIcon, isAction: true }),
      { name: 'Legal Library', icon: ScaleIcon, path: '/dataArchive/legal', permissionKey: 'Legal' },
      {
        // Renders through the Building Info shell, but the page itself is the
        // Asset tab strip's Mapping Hub.
        name: 'Mapping Hub',
        icon: MapIcon,
        path: '/buildingInfo/asset/mappingHub',
        permissionKey: 'ManageMappingHub',
        requiresProperty: true,
      },
      report('SupplierCheckInReport', { name: 'Supplier Check In', icon: PackageIcon, isAction: true }),
      { name: 'Visitor Log', icon: UserCheckIcon, path: '/visitorLog?type=Guest' },
    ],
  },
]

export const PORTFOLIO_CARD: Category = {
  title: 'ACCOUNT',
  description: 'Account, buildings, team, and analytics at the portfolio level',
  icon: AccountIcon,
  addButtons: [
    { label: 'Building', path: '/properties/create' },
    { label: 'Contact', path: '/portfolioTeam' },
    { label: 'Subscription', path: '/portfolioAnalytics/subscriptions' },
  ],
  items: [
    { name: 'Account Info', icon: AccountIcon, path: '/accountCreate' },
    { name: 'Buildings', icon: BuildingIcon, path: '/properties' },
    { name: 'Branding', icon: PaletteIcon, path: '/accountCreate?section=branding' },
    // The PCA report template is portfolio-level (specs#3296). ManagePcaHub only
    // reaches the permission hash when the portfolio has the PcaHub feature.
    { name: 'PCA Hub', icon: ClipboardListIcon, path: '/pcaHub', permissionKey: 'ManagePcaHub' },
    { name: 'Portfolio Team', icon: UsersIcon, path: '/portfolioTeam' },
    { name: 'Subscriptions', icon: FileTextIcon, path: '/portfolioAnalytics/subscriptions' },
  ],
}

export const PORTFOLIO_ANALYTICS_CARD: Category = {
  title: 'ANALYTICS',
  description: 'Portfolio-level analytics and reporting',
  icon: TrendingUpIcon,
  addButtonLabel: 'Work Order',
  addPath: '/workOrderDashboardGlobal',
  items: [
    report('GlobalAccountingDashboard', { name: 'Accounting', icon: TrendingUpIcon, isAction: true }),
    report('GlobalCapitalPlanning', { icon: TrendingUpIcon, isAction: true }),
    report('GlobalPortfolioRoundAnalytics', { icon: TrendingUpIcon, isAction: true }),
    report('GlobalCleaningIQ', { icon: TrendingUpIcon, isAction: true }),
    report('DeficiencyTrackerGlobal', { icon: TrendingUpIcon, isAction: true }),
    report('GlobalPortfolioRoundAnalyticsEngineering', { icon: TrendingUpIcon, isAction: true }),
    report('GlobalEngineeringIQ', { icon: TrendingUpIcon, isAction: true }),
    report('GlobalSecurityIQ', { icon: TrendingUpIcon, isAction: true }),
    report('GlobalEquipmentDashboard', { icon: TrendingUpIcon, isAction: true }),
    report('GlobalEquipmentDataIntegrity', { icon: TrendingUpIcon, isAction: true }),
    report('GlobalFeedbackDashboard', { name: 'Feedback', icon: TrendingUpIcon, isAction: true }),
    report('GlobalInspectionReport', { icon: TrendingUpIcon, isAction: true }),
    report('GlobalInsuranceDashboard', { name: 'Insurance', icon: TrendingUpIcon, isAction: true }),
    report('GlobalJobReport', { icon: TrendingUpIcon, isAction: true }),
    report('GlobalLeaseAbstraction', { icon: TrendingUpIcon, isAction: true }),
    report('GlobalPMCompliance', { icon: TrendingUpIcon, isAction: true }),
    report('GlobalPropertyCharacteristics', { icon: TrendingUpIcon, isAction: true }),
    report('GlobalRoofAbstraction', { icon: TrendingUpIcon, isAction: true }),
    report('GlobalComplianceRecords', { icon: ShieldCheckIcon, isAction: true }),
    report('GlobalPortfolioRoundAnalyticsSecurity', { icon: TrendingUpIcon, isAction: true }),
    report('GlobalTeamPerformance', { icon: TrendingUpIcon, isAction: true }),
    report('GlobalTenantHealthDashboard', { name: 'Tenant Health', icon: TrendingUpIcon, isAction: true }),
    report('GlobalTenantPerformanceDashboard', { name: 'Tenant Performance', icon: TrendingUpIcon, isAction: true }),
    report('GlobalWorkOrderDashboard', { name: 'Work Order', icon: TrendingUpIcon, isAction: true }),
  ],
}

export const ACTIVATE_CARD: Category = {
  title: 'ACTIVATE',
  description: 'Customer feedback, surveys, intelligence, and QR tools',
  icon: SparklesIcon,
  // The card shows for GlobalManageCustomers, but every destination except
  // /activateReport is gated on ActivateCustomerFeedback — without the key the
  // tiles rendered enabled and landed on Access Denied.
  // Tiles render in this order, so keep the list alphabetical by tile name.
  // The requiresProperty tiles are building-scoped tools re-homed here by the
  // 2026-08 nav reorg; unscoped paths resolve through the LegacyBuildingRedirect shims.
  items: [
    {
      name: 'Activate Notepad',
      icon: StickyNote2Icon,
      path: '/activateNotepad',
      permissionKey: ACTIVATE_PERMISSION,
    },
    {
      name: 'Customer Activity Report',
      icon: RateReviewIcon,
      path: '/customerActivity',
      permissionKey: ACTIVATE_PERMISSION,
    },
    { name: 'Customer Feedback', icon: FeedbackIcon, path: '/activateReport' },
    report('EquipmentDetailCollection', {
      icon: TrendingUpIcon,
      permissionKey: ACTIVATE_PERMISSION,
      requiresProperty: true,
      isAction: true,
    }),
    { name: 'Feedback Engine', icon: FeedbackIcon, path: '/feedbackEngine', permissionKey: ACTIVATE_PERMISSION },
    { name: 'Installation', icon: WrenchIcon, path: '/installationRound', permissionKey: ACTIVATE_PERMISSION },
    {
      name: 'Intelligence Portal',
      icon: TrendingUpIcon,
      path: '/intelligencePortal',
      permissionKey: ACTIVATE_PERMISSION,
    },
    { name: 'Lookbook', icon: FileTextIcon, path: '/lookbook', permissionKey: ACTIVATE_PERMISSION },
    {
      name: 'Master Library',
      icon: LibraryBooksIcon,
      path: '/masterLibrary/printLibrary',
      permissionKey: ACTIVATE_PERMISSION,
    },
    report('MasterNotepad', {
      icon: StickyNote2Icon,
      permissionKey: ACTIVATE_PERMISSION,
      requiresProperty: true,
      isAction: true,
    }),
    {
      name: 'Migration',
      icon: DatabaseIcon,
      path: '/dataArchive/migration',
      permissionKey: ACTIVATE_PERMISSION,
      requiresProperty: true,
    },
    { name: 'Pin Activity', icon: TagIcon, path: '/scans/pinActivity', permissionKey: ACTIVATE_PERMISSION },
    {
      name: 'Product Add-Ons',
      icon: PackageIcon,
      path: '/buildingInfo/activate/productAddOns',
      permissionKey: ACTIVATE_PERMISSION,
      requiresProperty: true,
    },
    { name: 'Prompt Engineering', icon: SparklesIcon, path: '/promptEngineering', permissionKey: ACTIVATE_PERMISSION },
    { name: 'QR Engineering', icon: QrCode2Icon, path: '/digitalProofReport', permissionKey: ACTIVATE_PERMISSION },
    { name: 'QR Location', icon: QrCodeScannerIcon, path: '/scans/qrLocation', permissionKey: ACTIVATE_PERMISSION },
    { name: 'QR Name', icon: QrCode2Icon, path: '/scans/qrName', permissionKey: ACTIVATE_PERMISSION },
    { name: 'Rating Report', icon: TrendingUpIcon, path: '/googleRatingReport', permissionKey: ACTIVATE_PERMISSION },
    { name: 'Report Builder', icon: FileTextIcon, path: '/reportBuilder', permissionKey: ACTIVATE_PERMISSION },
    {
      name: 'Subscriptions',
      icon: BellIcon,
      path: '/reportBuilder/subscriptions',
      permissionKey: ACTIVATE_PERMISSION,
    },
    { name: 'Survey', icon: RateReviewIcon, path: '/collectSurvey', permissionKey: ACTIVATE_PERMISSION },
    {
      name: 'Survey Builder',
      icon: LibraryBooksIcon,
      path: '/surveyLibrary/surveyBuilder',
      permissionKey: ACTIVATE_PERMISSION,
    },
    {
      name: 'Survey List',
      icon: ClipboardListIcon,
      path: '/surveyLibrary/surveyList',
      permissionKey: ACTIVATE_PERMISSION,
    },
    // Every customer's tickets (specs#3553): Activate staff only, like System Jobs.
    { name: 'Ticket Monitor', icon: TicketIcon, path: '/ticketMonitor', permissionKey: 'GlobalManageCustomers' },
    {
      name: 'Upsells',
      icon: TagIcon,
      path: '/buildingInfo/activate/upsells',
      permissionKey: ACTIVATE_PERMISSION,
      requiresProperty: true,
    },
  ],
}

/**
 * Platform administration, shown on the Activate view beside ACTIVATE_CARD.
 * Each tile carries the same Global* permission its Legacy sidebar twin does,
 * so a user who cannot open a page does not get an enabled tile for it.
 */
export const ACTIVATE_ADMIN_CARD: Category = {
  title: 'ACTIVATE ADMIN',
  description: 'Roles, feature flags, and platform administration',
  icon: AdminIcon,
  items: [
    { name: 'Feature Flags', icon: FlagIcon, path: '/admin/featureFlags', permissionKey: 'GlobalManageFeatureFlags' },
    { name: 'Impersonate User', icon: UserCheckIcon, path: '/impersonation', permissionKey: 'GlobalManageCustomers' },
    { name: 'Roles', icon: UsersIcon, path: '/admin/roles', permissionKey: 'GlobalManageRoles' },
    { name: 'System Jobs', icon: ClipboardListIcon, path: '/systemJobs', permissionKey: 'GlobalManageCustomers' },
    {
      name: 'System Settings',
      icon: SettingsIcon,
      path: '/admin/systemSettings',
      permissionKey: 'GlobalManageSystemSettings',
    },
  ],
}

export const API_METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH']
export const ADD_ICON = PlusIcon
